import i18n from '../i18n';

/**
 * Enhanced fetch wrapper that handles language-based messages
 * @param {string} url - The API endpoint URL
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<object>} - The response data with localized message
 */
export const apiFetch = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    const data = await response.json();

    // Get current language from i18n
    const currentLanguage = i18n.language || 'en';

    // Handle message localization
    if (data) {
      let localizedMessage = data.message; // Default message

      // Check if language-specific messages exist
      if (currentLanguage === 'es' && data.message_es) {
        localizedMessage = data.message_es;
      } else if (currentLanguage === 'en' && data.message_en) {
        localizedMessage = data.message_en;
      } else if (data.message_en) {
        // Fallback to English if current language message not available
        localizedMessage = data.message_en;
      }

      // Return enhanced data with localized message
      return {
        ...data,
        message: localizedMessage,
        ok: response.ok,
        status: response.status,
      };
    }

    return {
      ...data,
      ok: response.ok,
      status: response.status,
    };
  } catch (error) {
    // Network or parsing error
    throw new Error(error.message || 'Network error occurred');
  }
};

/**
 * POST request wrapper
 * @param {string} url - The API endpoint URL
 * @param {object} body - The request body
 * @param {object} headers - Additional headers (optional)
 * @returns {Promise<object>} - The response data
 */
export const apiPost = async (url, body, headers = {}) => {
  return apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(body),
  });
};

/**
 * GET request wrapper
 * @param {string} url - The API endpoint URL
 * @param {object} headers - Additional headers (optional)
 * @returns {Promise<object>} - The response data
 */
export const apiGet = async (url, headers = {}) => {
  return apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });
};

/**
 * PUT request wrapper
 * @param {string} url - The API endpoint URL
 * @param {object} body - The request body
 * @param {object} headers - Additional headers (optional)
 * @returns {Promise<object>} - The response data
 */
export const apiPut = async (url, body, headers = {}) => {
  return apiFetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(body),
  });
};

/**
 * PATCH request wrapper
 * @param {string} url - The API endpoint URL
 * @param {object} body - The request body
 * @param {object} headers - Additional headers (optional)
 * @returns {Promise<object>} - The response data
 */
export const apiPatch = async (url, body, headers = {}) => {
  return apiFetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(body),
  });
};

/**
 * DELETE request wrapper
 * @param {string} url - The API endpoint URL
 * @param {object} headers - Additional headers (optional)
 * @returns {Promise<object>} - The response data
 */
export const apiDelete = async (url, headers = {}) => {
  return apiFetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });
};

/**
 * Authenticated request wrapper (adds Authorization header)
 * @param {string} url - The API endpoint URL
 * @param {object} options - Fetch options
 * @param {string} token - Auth token
 * @returns {Promise<object>} - The response data
 */
export const apiAuthFetch = async (url, options = {}, token) => {
  const headers = {
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };

  return apiFetch(url, {
    ...options,
    headers,
  });
};
